import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SportBadge } from '@/components/SportBadge';
import { Clock, MapPin, Banknote, BadgeCheck, Calendar, Zap, Navigation } from 'lucide-react';
import { CourtSchedule, getDayLabel } from '@/hooks/useCourtSchedules';
import { calculateDistance, formatDistance } from '@/lib/geo-utils';

interface MiniCourtScheduleCardProps {
  schedule: CourtSchedule;
  onClick?: () => void;
  onQuickBook?: () => void;
  userLatitude?: number | null;
  userLongitude?: number | null;
}

export function MiniCourtScheduleCard({ schedule, onClick, onQuickBook, userLatitude, userLongitude }: MiniCourtScheduleCardProps) {
  const navigate = useNavigate();
  const court = schedule.court;
  const isVerified = court?.is_verified === true;

  const distance = userLatitude && userLongitude && court?.latitude && court?.longitude
    ? calculateDistance(userLatitude, userLongitude, court.latitude, court.longitude)
    : null;

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (court) {
      navigate(`/courts/detail/${court.id}`);
    }
  };

  const handleQuickBook = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onQuickBook) {
      onQuickBook();
    } else if (court) {
      navigate(`/courts/detail/${court.id}?schedule=${schedule.id}`);
    }
  };

  return (
    <Card 
      className="glass-card overflow-hidden hover:shadow-md transition-all cursor-pointer"
      onClick={handleClick}
    >
      <CardContent className="p-3">
        <div className="flex gap-3">
          {/* Court Image */}
          {court?.image_url && (
            <div 
              className="w-16 h-16 rounded-lg bg-cover bg-center flex-shrink-0"
              style={{ backgroundImage: `url(${court.image_url})` }}
            />
          )}
          
          <div className="flex-1 min-w-0">
            {/* Court name and sport */}
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-sm text-foreground truncate">
                {court?.name || 'Sân không xác định'}
              </h4>
              {court && <SportBadge sport={court.sport} size="sm" />}
              {isVerified && (
                <BadgeCheck className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              )}
            </div>
            
            {/* Schedule: Day & Time */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-primary" />
                <span className="font-medium text-foreground">{getDayLabel(schedule.day_of_week)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>{schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)}</span>
              </div>
            </div>

            {/* Location & Distance & Price & Quick Book */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{court?.location}</span>
                {distance !== null && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 gap-0.5 flex-shrink-0 ml-1">
                    <Navigation className="w-2.5 h-2.5 text-primary" />
                    {formatDistance(distance)}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="flex items-center gap-1 text-xs font-medium text-primary">
                  <Banknote className="w-3 h-3" />
                  <span>{schedule.hourly_price.toLocaleString()}đ</span>
                </div>
                <Button 
                  size="sm" 
                  variant="default"
                  className="h-6 px-2 text-xs"
                  onClick={handleQuickBook}
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Đặt nhanh
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
